import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import DeliveryNotification from '../components/DeliveryNotification';

const HowItWorks = () => {
  const [postalCode, setPostalCode] = useState('');
  const [deliveryAvailable, setDeliveryAvailable] = useState(null);

  // ✅ Refs pour les animations au scroll
  const sectionRefs = {
    hero: useRef(null),
    checkZone: useRef(null),
    deliveryZone: useRef(null),
    slots: useRef(null),
    deposit: useRef(null),
    faq: useRef(null),
    cta: useRef(null),
  };

  // ✅ State pour suivre les sections visibles
  const [visibleSections, setVisibleSections] = useState({});

  // ✅ Observer les sections pour les animations au scroll
  useEffect(() => {
    const observers = {};
    const sectionIds = ['hero', 'checkZone', 'deliveryZone', 'slots', 'deposit', 'faq', 'cta'];

    sectionIds.forEach((id) => {
      const element = sectionRefs[id].current;
      if (element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setVisibleSections((prev) => ({ ...prev, [id]: true }));
              }
            });
          },
          { 
            threshold: 0.15,
            rootMargin: '0px 0px -50px 0px'
          }
        );
        observer.observe(element);
        observers[id] = observer;
      }
    });

    return () => {
      sectionIds.forEach((id) => {
        if (observers[id]) {
          observers[id].disconnect();
        }
      });
    };
  }, []);

  const checkDelivery = () => {
    const availableZones = ['93360', '93330', '93220', '93250', '93110', '93100', '93160', '93340', '94170', '94130', '94360', '94500', '94120', '94300', '94160', '77500'];
    setDeliveryAvailable(availableZones.includes(postalCode));
  };

  const Icons = {
    CheckCircle: () => (
      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    Clock: () => (
      <svg className="w-12 h-12 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    Morning: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    Afternoon: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
      </svg>
    ),
    Flexible: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    Door: () => (
      <svg className="w-12 h-12 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    Map: () => (
      <svg className="w-12 h-12 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
    ChevronRight: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    )
  };

  const faqs = [
    { q: "Livrez-vous dans ma commune ?", a: "Entrez votre code postal pour vérifier l'éligibilité." },
    { q: "Quels sont les jours de livraison ?", a: "Les livraisons ont lieu du Lundi au Vendredi à partir de 18h Et les samedis et Dimanche à partir de 08h. Les créneaux disponibles sont visibles lors de la commande." },
    { q: "Y a-t-il une heure de livraison précise ?", a: "Une fourchette horaire vous sera communiquée la veille de la livraison par email." },
    { q: "Que se passe-t-il si je suis absent ?", a: "Avec l'option Dépôt devant la porte, votre commande sera déposée à l'endroit indiqué. Sinon, nous repasserons le lendemain." },
    { q: "Puis-je modifier mon créneau après la commande ?", a: "Oui, jusqu'à la veille de la livraison via votre espace client ou en contactant notre service client." },
    { q: "Que faire si je suis hors zone de livraison ?", a: "Notre zone s'agrandit régulièrement. Inscrivez-vous à notre newsletter pour être informé des nouvelles zones." }
  ];

  return (
    <div>
      {/* ✅ Hero Section - Animation fade-in */}
      <div 
        ref={sectionRefs.hero}
        className={`relative bg-gradient-to-r from-primary to-secondary py-20 transition-all duration-1000 ${
          visibleSections.hero ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 font-playfair animate-fade-in-up">
                Livraison de packs d'eau à domicile
              </h1>
              <p className="text-lg mb-6 text-gray-200 animate-fade-in-up animation-delay-200">
                Livraison rapide et fiable de vos packs d'eau, sodas et laits directement chez vous.
              </p>
              <Link to="/products" className="inline-flex items-center gap-2 bg-secondary text-primary px-6 py-3 rounded-full font-semibold hover:scale-105 transition duration-300 animate-fade-in-up animation-delay-400">
                Commander maintenant <Icons.ChevronRight />
              </Link>
            </div>
            <div className="relative group">
              <img 
                src="https://i.pinimg.com/1200x/f1/91/72/f19172a89c0fba83f353c9ce2dbf5377.jpg" 
                alt="Livraison de packs d'eau"
                className="rounded-2xl shadow-2xl w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section Vérification zone - Animation slide-up */}
      <div 
        ref={sectionRefs.checkZone}
        className={`py-20 bg-cream transition-all duration-1000 ${
          visibleSections.checkZone ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Vérifier la livraison dans votre ville</h2>
              <p className="text-gray-600 mb-6">Entrez votre code postal pour voir si Aquahomedelivery livre chez vous.</p>
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  placeholder="Code postal (Ex: 68100)"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  maxLength="5"
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:border-secondary transition-all duration-300 focus:scale-105"
                />
                <button 
                  onClick={checkDelivery} 
                  className="bg-primary text-white px-6 py-3 rounded-full hover:bg-opacity-90 transition hover:scale-105 duration-300"
                >
                  Vérifier
                </button>
              </div>
              {deliveryAvailable !== null && (
  <div className="mt-4">
    {deliveryAvailable ? (
      <p className="text-green-600 animate-fade-in">✓ Bonne nouvelle ! Nous livrons dans votre zone</p>
    ) : (
      <DeliveryNotification 
        postalCode={postalCode}
        onClose={() => setDeliveryAvailable(null)}
      />
    )}
  </div>
)}
              <p className="text-sm text-gray-400 mt-2">5 chiffres, sans espace.</p>
            </div>
            <div className="relative group">
              <img 
                src="https://i.pinimg.com/736x/ca/14/80/ca14804ac8227ff2f9d1e563edd2136d.jpg" 
                alt="Carte zone de livraison"
                className="rounded-2xl shadow-lg w-full transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg animate-pulse">
                  Zone de livraison
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section Zone de livraison - Animation slide-up */}
      <div 
        ref={sectionRefs.deliveryZone}
        className={`py-20 bg-white transition-all duration-1000 ${
          visibleSections.deliveryZone ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 group">
              <img 
                src="https://i.pinimg.com/736x/a6/b5/64/a6b5640973d36745d4e4a3dab6f82bf5.jpg" 
                alt="Carte détaillée zone de livraison"
                className="rounded-2xl shadow-lg w-full transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-secondary transition-transform duration-500 hover:rotate-12">
                  <Icons.Map />
                </div>
                <h2 className="text-3xl font-bold">Zone de livraison</h2>
              </div>
              <p className="text-gray-600 mb-4">
                AquaHomeDelivery est un service de livraison locale de packs d'eau à domicile, organisé pour garantir des tournées fiables et ponctuelles.
              </p>
              <p className="text-gray-600 mb-4">
                L'éligibilité est automatiquement vérifiée à partir du code postal saisi lors de la commande.
              </p>
              <p className="text-gray-600">
                Toute adresse située en dehors de cette zone ne pourra pas être validée. Cette zone de livraison a vocation à s'élargir progressivement afin de desservir de nouvelles communes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section Créneaux - Animation slide-up */}
      <div 
        ref={sectionRefs.slots}
        className={`py-20 bg-pearl transition-all duration-1000 ${
          visibleSections.slots ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Créneaux de livraison</h2>
              <p className="text-gray-600 mb-6">
                Choisissez votre préférence de livraison directement lors de la commande. Aquahomedelivery vous permet de sélectionner le créneau le plus adapté à votre organisation au moment du paiement.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="text-secondary transition-transform duration-500 hover:rotate-12"><Icons.Morning /></div>
                  <div>
                    <h3 className="font-bold text-lg">Lundi au Vendredi</h3>
                    <p className="text-gray-500 text-sm">Livraison entre 18h et 22h (par tranche de 2h)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-105 delay-100">
                  <div className="text-secondary transition-transform duration-500 hover:rotate-12"><Icons.Afternoon /></div>
                  <div>
                    <h3 className="font-bold text-lg">Samedi et Dimanche</h3>
                    <p className="text-gray-500 text-sm">Livraison entre 08h à 22h (par tranche de 2h)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-105 delay-200">
                  <div className="text-secondary transition-transform duration-500 hover:rotate-12"><Icons.Flexible /></div>
                  <div>
                    <h3 className="font-bold text-lg">Flexible</h3>
                    <p className="text-gray-500 text-sm">Toute la journée, optimisé selon les tournées</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                Les jours de livraison sont définis à l'avance et seuls les créneaux disponibles peuvent être sélectionnés.
              </p>
            </div>
            <div className="group">
              <img 
                src="https://i.pinimg.com/736x/a6/b5/64/a6b5640973d36745d4e4a3dab6f82bf5.jpg" 
                alt="Calendrier livraison"
                className="rounded-2xl shadow-lg w-full transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section Dépôt devant la porte - Animation slide-up */}
      <div 
        ref={sectionRefs.deposit}
        className={`py-20 bg-white transition-all duration-1000 ${
          visibleSections.deposit ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 group">
              <img 
                src="https://i.pinimg.com/1200x/22/37/d1/2237d16199872738e2d2cfdf35cb3832.jpg" 
                alt="Dépôt devant la porte"
                className="rounded-2xl shadow-lg w-full transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-secondary transition-transform duration-500 hover:rotate-12">
                  <Icons.Door />
                </div>
                <h2 className="text-3xl font-bold">Dépôt devant la porte</h2>
              </div>
              <p className="text-gray-600 mb-4 font-semibold">Gagnez du temps, même en cas d'absence.</p>
              <p className="text-gray-600 mb-4">
                AquaHomeDelivery vous propose l'option dépôt devant la porte lors de la commande, pour recevoir vos packs sans contrainte.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  'Option sélectionnable au moment du paiement',
                  'Information enregistrée directement dans la commande',
                  'Livraison possible même si vous êtes absent',
                  'Vos packs sont déposés devant votre domicile, à l\'endroit indiqué'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-2 hover:translate-x-2 transition-transform duration-300">
                    <Icons.CheckCircle /> {item}
                  </li>
                ))}
              </ul>
              <p className="text-gray-600">
                Cette option est particulièrement adaptée aux personnes absentes en journée. Elle permet d'éviter toute reprogrammation et assure la réception de votre commande sans contrainte.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Section FAQ - Animation slide-up avec stagger */}
      <div 
        ref={sectionRefs.faq}
        className={`py-20 bg-pearl transition-all duration-1000 ${
          visibleSections.faq ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center mb-12">Questions fréquentes</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-105"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="font-bold text-lg mb-2 text-primary">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ✅ Section CTA - Animation slide-up */}
      <div 
        ref={sectionRefs.cta}
        className={`py-20 bg-gradient-to-r from-primary to-secondary transition-all duration-1000 ${
          visibleSections.cta ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-4 animate-fade-in-up">Prêt à commander votre pack d'eau ?</h2>
          <p className="text-gray-200 mb-8 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
            Choisissez vos packs, sélectionnez votre créneau de livraison et recevez votre eau directement à domicile
          </p>
          <Link 
            to="/products" 
            className="inline-flex items-center gap-2 bg-secondary text-primary px-8 py-3 rounded-full font-semibold hover:scale-105 transition duration-300 animate-fade-in-up animation-delay-400 hover:shadow-2xl"
          >
            Voir les packs <Icons.ChevronRight />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;